/**
 * Cobros. 3 KPI + tabla de rent roll.
 *
 * La tabla muestra los cargos del mes MÁS los impagados arrastrados de meses anteriores, que es
 * como se lee un rent roll de verdad y lo que reproduce el maquetado (una fila vencida el mes
 * pasado junto a los vencimientos de este). Las filas arrastradas se marcan.
 */
import { useQuery } from '@tanstack/react-query';
import type { RentChargeStatus, RentRollRow } from '@naar/shared';
import { api, queryKeys } from '../api/client';
import { PageHeader } from '../components/PageHeader';
import { Card } from '../components/Card';
import { KpiCard, KpiStrip } from '../components/KpiCard';
import { Avatar } from '../components/Avatar';
import { Badge, type BadgeTone } from '../components/Badge';
import { DataTable, IdentityCell, cellStyles } from '../components/DataTable';
import { EmptyState, ErrorState } from '../components/EmptyState';
import { SkeletonCard, SkeletonKpiRow, SkeletonPageHeader } from '../components/Skeleton';
import { formatEur, formatPercent } from '../lib/format';

/** Plantilla de columnas literal del maquetado. */
const TEMPLATE = '2fr 1.3fr 1fr 1fr 1.1fr';

const COLUMNS = [
  { key: 'tenant', label: 'Inquilino' },
  { key: 'property', label: 'Propiedad' },
  { key: 'rent', label: 'Renta' },
  { key: 'due', label: 'Vence' },
  { key: 'status', label: 'Estado', align: 'right' as const },
];

const STATUS_TONE: Record<RentChargeStatus, BadgeTone> = {
  cobrado: 'success',
  emitido: 'warning',
  pendiente: 'neutral',
  parcial: 'warning',
  impagado: 'danger',
  anulado: 'neutral',
};

export function RentCollectionPage() {
  const { data, isPending, error, refetch } = useQuery({
    queryKey: queryKeys.rentCharges(),
    queryFn: () => api.rentCharges(),
  });

  const header = (
    <PageHeader
      title="Cobro de alquileres"
      subtitle="Domiciliaciones, recordatorios y recibos de renta automáticos."
    />
  );

  if (isPending) {
    return (
      <section>
        <SkeletonPageHeader />
        <SkeletonKpiRow count={3} />
        <SkeletonCard rows={5} height={46} />
      </section>
    );
  }

  if (error) {
    return (
      <section>
        {header}
        <ErrorState error={error} onRetry={() => void refetch()} />
      </section>
    );
  }

  const { totals } = data;

  return (
    <section>
      {header}

      <KpiStrip columns={3} last data-tour="cobros">
        <KpiCard
          label="Cobrado este mes"
          value={formatEur(totals.collected)}
          trend={`${formatPercent(totals.collectedPercent, 0)} de la renta esperada`}
          trendDirection="up"
        />
        <KpiCard
          label="Pendiente de cobro"
          value={formatEur(totals.pending)}
          tone="warning"
          note={
            totals.pendingCount === 1
              ? '1 recibo en curso'
              : `${totals.pendingCount} recibos en curso`
          }
        />
        <KpiCard
          label="Impagos"
          value={formatEur(totals.overdue)}
          tone="danger"
          trend={
            totals.overdueCount === 0
              ? 'Sin impagos'
              : `${totals.overdueTenants} ${
                  totals.overdueTenants === 1 ? 'inquilino' : 'inquilinos'
                } · ${totals.maxDaysOverdue} días`
          }
          trendDirection={totals.overdueCount === 0 ? 'flat' : 'down'}
        />
      </KpiStrip>

      <Card variant="table">
        {data.data.length === 0 ? (
          <EmptyState
            inline
            icon="euro"
            title={`No hay cargos de renta en ${data.monthLabel}`}
            body="Los recibos aparecerán aquí en cuanto haya contratos activos con cargos emitidos."
          />
        ) : (
          <DataTable<RentRollRow>
            caption={`Rent roll de ${data.monthLabel}`}
            columns={COLUMNS}
            template={TEMPLATE}
            rows={data.data}
            rowKey={(row) => row.id}
            renderRow={(row) => (
              <>
                <IdentityCell
                  size="sm"
                  visual={<Avatar initials={row.tenant?.initials ?? '??'} size={38} fontSize={13} />}
                  main={row.tenant?.fullName ?? 'Sin titular'}
                  sub={row.carriedOver ? `Arrastre · ${row.daysOverdue} días de retraso` : null}
                />
                <span className={cellStyles.cellSecondary}>{row.property?.name ?? '—'}</span>
                <span className={cellStyles.cellMedium}>{formatEur(row.amount)}</span>
                <span className={cellStyles.cellMuted}>{row.dueLabel}</span>
                <span className={cellStyles.rightSelf}>
                  <Badge tone={STATUS_TONE[row.status]}>{row.statusLabel}</Badge>
                </span>
              </>
            )}
            /* Móvil: quién y en qué piso a la izquierda, el estado a la derecha; debajo, importe y vencimiento. */
            renderCard={(row) => (
              <>
                <div className={cellStyles.cardHead}>
                  <IdentityCell
                    size="sm"
                    visual={<Avatar initials={row.tenant?.initials ?? '??'} size={38} fontSize={13} />}
                    main={row.tenant?.fullName ?? 'Sin titular'}
                    sub={row.property?.name ?? null}
                  />
                  <Badge tone={STATUS_TONE[row.status]}>{row.statusLabel}</Badge>
                </div>
                <div className={cellStyles.cardStats}>
                  <span>
                    <span className={cellStyles.cardStatLabel}>Renta</span>
                    <span className={cellStyles.cardStatValue}>{formatEur(row.amount)}</span>
                  </span>
                  <span>
                    <span className={cellStyles.cardStatLabel}>Vence</span>
                    <span className={cellStyles.cardStatValue}>{row.dueLabel}</span>
                  </span>
                  {row.carriedOver ? (
                    <span>
                      <span className={cellStyles.cardStatLabel}>Retraso</span>
                      <span className={cellStyles.cardStatValue}>{row.daysOverdue} días</span>
                    </span>
                  ) : null}
                </div>
              </>
            )}
          />
        )}
      </Card>
    </section>
  );
}
